import { Avatar, Badge, CircularProgress, makeStyles, Theme, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import classNames from 'classnames';
import React, { useEffect } from 'react';

import { ReactComponent as ChangeEventCommentSvg } from '../../../assets/change_event_comment.svg';
import { ReactComponent as ChangeEventEditSvg } from '../../../assets/change_event_edit.svg';
import { ReactComponent as ChangeEventPostFaqSvg } from '../../../assets/change_event_post_faq.svg';
import { ReactComponent as ChannelSvg } from '../../../assets/channel.svg';
import { ReactComponent as FaqSvg } from '../../../assets/faq.svg';
import { ReactComponent as PostSvg } from '../../../assets/post.svg';
import { ReactComponent as StarSvg } from '../../../assets/star.svg';
import {
  ChangeEventType,
  PlexMemberRole,
  PlexSummaryChangeEventFragment,
  PlexSummaryFragment,
  useGetPlexSummaryLazyQuery,
} from '../../../graphql/gen/types';
import { selectPlexFilterItem } from '../../../models/filters/actions/selectPlexFilterItem';
import { showPlexSummary } from '../../../models/filters/actions/showPlexSummary';
import { Dispatch, useFiltersState } from '../../../models/filters/useFiltersState';
import { colorPalette, leftBorderStyle, spacings, textSizes, TextSizesEnum } from '../../../theme';
import { rolesRankingSort } from '../../../utils/permissions';
import { ButtonColor } from '../../shared/primitives/PlexButton';
import { IconButtonSize, PlexIconButton } from '../../shared/primitives/PlexIconButton';
import { PlexTypography } from '../../shared/primitives/PlexTypography';
import { Error as ErrorView } from '../shared/Error';
import { ChangeEvent } from './ChangeEvent';

interface Props {
  plexPermalink: string;
}

export const PlexSummary = React.memo(({ plexPermalink }: Props) => {
  const classes = useStyles();
  const [, dispatch] = useFiltersState();
  const [
    getPlex,
    { data: plexQueryData, loading: plexQueryLoading, error: plexQueryError },
  ] = useGetPlexSummaryLazyQuery({ errorPolicy: 'all' });

  useEffect(() => {
    if (!plexPermalink) {
      return;
    }
    getPlex({ variables: { plexId: plexPermalink } });
  }, [getPlex, plexPermalink]);

  useCloseOnDelete({
    dispatch,
    loading: plexQueryLoading,
    plexPermalink,
    deleted: plexQueryData?.loggedIn?.plexById?.deleted || false,
  });

  let componentView = <div>...</div>;
  if (plexQueryLoading) {
    componentView = <CircularProgress size={24} />;
  } else if (plexQueryData == null && plexQueryError) {
    componentView = <ErrorView error={plexQueryError} />;
  } else if (!plexQueryData) {
    componentView = <div>No data</div>;
  } else {
    const plexData: PlexSummaryFragment | null | undefined = plexQueryData?.loggedIn?.plexById;

    if (plexData == null) {
      // We are not loading and we did not get an error.
      // This should be illegal state.
      // TODO: redirect to different URL if this occurs
      throw new Error(`Illegal state: ${plexQueryError} ${plexQueryLoading} ${plexQueryData}`);
    }

    const maxAvatars = 6;
    const membersCount = plexData.summary.members.length || 0;
    const getEventIcon = (event: PlexSummaryChangeEventFragment): React.ReactNode => {
      switch (event.eventType) {
        case ChangeEventType.Create:
          switch (event.eventTarget.__typename) {
            case 'ChangeEventTargetPost':
            case 'ChangeEventTargetEvent':
            case 'ChangeEventTargetQuestion':
              return <ChangeEventPostFaqSvg className={classes.changeEventIcon} />;
            case 'ChangeEventTargetComment':
            case 'ChangeEventTargetAnswer':
              return <ChangeEventCommentSvg className={classes.changeEventIcon} />;
          }
          break;
        case ChangeEventType.Update:
          switch (event.eventTarget.__typename) {
            case 'ChangeEventTargetPost':
            case 'ChangeEventTargetEvent':
            case 'ChangeEventTargetQuestion':
              return <ChangeEventEditSvg className={classes.changeEventIcon} />;
            case 'ChangeEventTargetComment':
            case 'ChangeEventTargetAnswer':
              return <ChangeEventCommentSvg className={classes.changeEventIcon} />;
          }
          break;
      }
      return <ChangeEventEditSvg className={classes.changeEventIcon} />;
    };

    componentView = (
      <>
        <div className={classes.topBar}>
          <PlexTypography className={classes.summaryText} text={'Plex Summary'} size={TextSizesEnum.Large} />
          <PlexIconButton
            onClick={(): void => {
              dispatch(showPlexSummary({ permalink: undefined }));
            }}
            MuiIcon={CloseIcon}
            color={ButtonColor.TransparentGray}
            tooltip={'Close'}
            size={IconButtonSize.Normal}
            tooltipPlacement="left"
          />
        </div>
        <PlexTypography className={classes.plexTitle} text={plexData.name} size={TextSizesEnum.XXXLarge} />
        <PlexTypography
          className={classes.plexDescription}
          text={plexData.description}
          size={TextSizesEnum.NormalPlus}
        />
        <div className={classes.countersContainer}>
          <PostSvg className={classes.counterIcon} />
          <PlexTypography
            className={classes.counterText}
            text={plexData.summary.postsCount === 1 ? '1 Post' : `${plexData.summary.postsCount} Posts`}
            size={TextSizesEnum.Normal}
          />
          <FaqSvg className={classes.counterIcon} />
          <PlexTypography
            className={classes.counterText}
            text={`${plexData.summary.faqsCount} FAQs`}
            size={TextSizesEnum.Normal}
          />
          <ChannelSvg className={classes.counterIcon} />
          <PlexTypography
            className={classes.counterText}
            text={`${plexData.summary.channelsCount} Channels`}
            size={TextSizesEnum.Normal}
          />
        </div>
        <PlexTypography className={classes.zoneTitle} text={'Member'} size={TextSizesEnum.NormalPlus} />
        <div className={classes.membersList}>
          {plexData.summary.members
            // copy array so we don't modify the cache
            .slice()
            .sort(rolesRankingSort)
            .slice(0, maxAvatars)
            .map((member) => {
              if (member.role === PlexMemberRole.Owner || member.role === PlexMemberRole.Manager) {
                return (
                  <Tooltip key={`${member.picture}_${member.name}`} title={member.name}>
                    <Badge
                      overlap="circle"
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      className={classes.avatarContainer}
                      classes={{ anchorOriginBottomRightCircle: classes.starBadge }}
                      badgeContent={<StarSvg />}
                    >
                      <Avatar className={classes.avatar} src={member.picture} alt={member.name} />
                    </Badge>
                  </Tooltip>
                );
              } else {
                return (
                  <Tooltip key={`${member.picture}_${member.name}`} title={member.name}>
                    <Avatar
                      key={member.name}
                      className={classNames(classes.avatar, classes.avatarContainer)}
                      src={member.picture}
                      alt={member.name}
                    />
                  </Tooltip>
                );
              }
            })}
          {membersCount > maxAvatars && (
            <Avatar className={classNames(classes.avatar, classes.avatarContainer)}>{`+${Math.min(
              membersCount - maxAvatars,
              99
            )}`}</Avatar>
          )}
        </div>
        <PlexTypography className={classes.zoneTitle} text={'Activity'} size={TextSizesEnum.NormalPlus} />
        <div className={classes.changeEventsContainer}>
          {plexData.summary.events.map((event) => (
            <div key={event.id} className={classes.changeEventContainer}>
              <div className={classes.changeEventTimeline}>
                {getEventIcon(event)}
                <div className={classes.changeEventConnector} />
              </div>
              <ChangeEvent className={classes.changeEvent} event={event} />
            </div>
          ))}
        </div>
      </>
    );
  }
  return <div className={classes.root}>{componentView}</div>;
});

export const PLEX_SUMMARY_WIDTH = 390;

function useCloseOnDelete({
  dispatch,
  loading,
  plexPermalink,
  deleted,
}: {
  dispatch: Dispatch;
  loading: boolean;
  plexPermalink: string;
  deleted: boolean;
}): void {
  useEffect(() => {
    if (!loading && deleted) {
      dispatch(showPlexSummary({ permalink: undefined }));
      dispatch(selectPlexFilterItem({ permalink: plexPermalink, selected: false }));

      // TODO(https://github.com/all-hands-hq/source/issues/342): Further improvements
    }
  }, [deleted, dispatch, loading, plexPermalink]);
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    width: PLEX_SUMMARY_WIDTH,
    backgroundColor: '#fff',
    overflowY: 'auto',
    ...leftBorderStyle,
  },
  topBar: {
    display: 'flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  summaryText: {
    color: colorPalette.dark._700,
    fontWeight: 600,
    letterSpacing: 0.15,
    marginLeft: spacings.xlarge,
    marginRight: spacings.xlarge,
    marginTop: spacings.xlarge,
  },
  plexTitle: {
    color: colorPalette.dark._800,
    fontWeight: 'bold',
    letterSpacing: 0.25,
    marginLeft: spacings.xlarge,
    flexShrink: 0,
  },
  plexDescription: {
    color: '#464663',
    fontWeight: 500,
    letterSpacing: 0.1,
    marginLeft: spacings.xlarge,
    marginTop: spacings.normal,
    flexShrink: 0,
  },
  countersContainer: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'row',
    marginLeft: spacings.xlarge,
    paddingTop: spacings.xlarge,
    paddingBottom: spacings.xlarge,
    alignItems: 'center',
  },
  counterIcon: {
    color: colorPalette.dark._800,
    fontWeight: 500,
    marginRight: spacings.small,
    fill: `${colorPalette.dark._800} !important`, // sets the svg color
  },
  counterText: {
    color: colorPalette.dark._800,
    fontWeight: 500,
    marginRight: spacings.xlarge,
  },
  zoneTitle: {
    marginLeft: spacings.xlarge,
    marginTop: spacings.normal,
    color: colorPalette.purpleGray._500,
    fontWeight: 600,
    letterSpacing: 0.1,
    flexShrink: 0,
  },
  membersList: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'row',
    marginLeft: spacings.xlarge,
    marginBottom: spacings.xlarge,
    marginTop: spacings.small,
  },
  avatar: {
    display: 'flex',
    flexShrink: 0,
    width: 32,
    height: 32,
    fontSize: textSizes[TextSizesEnum.Small].fontSize,
    lineHeight: textSizes[TextSizesEnum.Small].lineHeight,
    fontWeight: 500,
  },
  avatarContainer: {
    display: 'flex',
    flexShrink: 0,
    marginRight: spacings.small,
    justifyContent: 'center',
  },
  starBadge: {
    transform: `unset`,
    bottom: 'unset',
    top: '58%',
    right: 'unset',
    display: 'flex',
    padding: 0,
  },
  changeEventsContainer: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'column',
    marginTop: spacings.xlarge,
  },
  changeEventContainer: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'row',
    marginLeft: spacings.xlarge,
  },
  changeEventTimeline: {
    display: 'flex',
    flexShrink: 0,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: spacings.normal,
  },
  changeEventIcon: {
    display: 'flex',
    margin: spacings.xsmall,
    flexShrink: 0,
  },
  changeEventConnector: {
    display: 'flex',
    flexShrink: 0,
    width: 2,
    height: '100%',
    backgroundColor: colorPalette.purpleGray._100,
  },
  changeEvent: {
    marginBottom: spacings.xxlarge,
  },
}));
